import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { API, formatDate } from "../lib/api";

const PRINT_DATE = () => new Date().toLocaleString("en-IN", {
  day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
});

export default function ReportPrint() {
  const { type } = useParams();
  const [params] = useSearchParams();
  const [report, setReport] = useState(null);
  const [store, setStore] = useState(null);

  useEffect(() => {
    const qp = {};
    if (params.get("date_from")) qp.date_from = params.get("date_from");
    if (params.get("date_to")) qp.date_to = params.get("date_to");
    if (params.get("vendor_id")) qp.vendor_id = params.get("vendor_id");
    Promise.all([
      API.get(`/reports/${type}`, { params: qp }),
      API.get("/settings"),
    ]).then(([r, st]) => { setReport(r.data); setStore(st.data); });
  }, [type, params]);

  useEffect(() => {
    if (report && store && params.get("auto") === "1") {
      const t = setTimeout(() => window.print(), 500);
      return () => clearTimeout(t);
    }
  }, [report, store, params]);

  if (!report || !store) return <div className="p-8 text-sm text-slate-500">Loading…</div>;

  const isAmount = (h) => /amount|total|debit|credit|paid|due|mrp|purchase|balance|revenue/i.test(h);
  const isQty = (h) => /qty|quantity|units|items|count/i.test(h);

  return (
    <div className="bg-slate-100 min-h-screen p-6 print:bg-white print:p-0" data-testid="report-print">
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 12mm; }
          .noprint { display: none !important; }
          body { background: white !important; }
          .sheet { box-shadow: none !important; border-radius: 0 !important; padding: 0 !important; max-width: none !important; }
          .sheet thead { display: table-header-group; }
          .sheet table tr { page-break-inside: avoid; }
        }
        .sheet { background: white; max-width: 980px; margin: 0 auto; padding: 36px 40px; box-shadow: 0 8px 24px rgba(0,0,0,.08); border-radius: 12px; font-family: 'IBM Plex Sans', Arial, sans-serif; color: #0F172A; }
        .h-band { display: flex; justify-content: space-between; align-items: flex-end; padding-bottom: 14px; border-bottom: 3px double #002FA7; margin-bottom: 18px; }
        .brand { font-family: 'Manrope', sans-serif; font-weight: 800; font-size: 26px; color: #002FA7; letter-spacing: -0.02em; line-height: 1; }
        .meta { font-size: 11px; color: #475569; margin-top: 4px; line-height: 1.55; }
        .badge { display:inline-block; padding: 4px 10px; border-radius: 4px; background: #EFF6FF; color: #1D4ED8; font-size: 10px; font-weight: 800; letter-spacing: .15em; text-transform: uppercase; }
        .rtitle { font-family: 'Manrope', sans-serif; font-size: 22px; font-weight: 700; letter-spacing: -.01em; margin-top: 6px; }
        .rmeta { font-size: 11px; color: #64748B; }
        .summary { display: flex; flex-wrap: wrap; gap: 18px 36px; margin: 14px 0 18px; padding: 14px 18px; background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 8px; }
        .summary .cell { min-width: 140px; }
        .summary .cell label { display:block; font-size: 9.5px; font-weight: 700; letter-spacing: .14em; text-transform: uppercase; color: #64748B; margin-bottom: 2px; }
        .summary .cell span { font-family: 'Manrope', sans-serif; font-size: 16px; font-weight: 700; color: #0F172A; }
        table.r { width: 100%; border-collapse: collapse; font-size: 12px; }
        table.r thead th {
          background: #002FA7; color: white;
          text-align: left; padding: 8px 10px;
          font-weight: 600; font-size: 10.5px;
          text-transform: uppercase; letter-spacing: .04em;
          border-right: 1px solid rgba(255,255,255,.2);
        }
        table.r thead th:last-child { border-right: 0; }
        table.r tbody td { padding: 7px 10px; border-bottom: 1px solid #E2E8F0; }
        table.r tbody tr:nth-child(even) td { background: #F8FAFC; }
        td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; }
        td.qty, th.qty { text-align: right; font-variant-numeric: tabular-nums; }
        .footer { margin-top: 22px; display: flex; justify-content: space-between; gap: 16px; font-size: 10px; color: #475569; border-top: 1px dashed #CBD5E1; padding-top: 14px; }
        .signbox { width: 200px; padding-top: 36px; border-top: 1px solid #94A3B8; text-align: center; font-size: 10px; color: #475569; }
        .toolbar { max-width: 980px; margin: 0 auto 14px; display: flex; gap: 8px; justify-content: flex-end; }
      `}</style>

      <div className="toolbar noprint">
        <button onClick={() => window.print()} className="btn-primary" data-testid="report-print-now">🖨️ Print</button>
        <button onClick={() => window.close()} className="btn-ghost">Close</button>
      </div>

      <div className="sheet">
        <div className="h-band">
          <div>
            <div className="brand">{store.name}</div>
            <div className="meta">
              {store.address}<br />
              {store.gst && <>GST: <b>{store.gst}</b> &nbsp;·&nbsp; </>}
              {store.contact && <>Contact: <b>{store.contact}</b> &nbsp;·&nbsp; </>}
              {store.email}
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div className="badge">Report</div>
            <div className="rtitle">{report.title}</div>
            <div className="rmeta">{report.subtitle}</div>
            <div className="rmeta" style={{ marginTop: 2 }}>Generated: {PRINT_DATE()}</div>
          </div>
        </div>

        {report.summary && Object.keys(report.summary).length > 0 && (
          <div className="summary">
            {Object.entries(report.summary).map(([k, v]) => (
              <div className="cell" key={k}>
                <label>{k}</label>
                <span>{v}</span>
              </div>
            ))}
          </div>
        )}

        {report.rows.length === 0 ? (
          <div style={{ padding: 32, textAlign: "center", color: "#64748B", fontSize: 13, fontStyle: "italic" }}>
            No records for the selected criteria.
          </div>
        ) : (
          <table className="r">
            <thead>
              <tr>
                {report.headers.map((h) => (
                  <th key={h} className={isAmount(h) ? "num" : isQty(h) ? "qty" : ""}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {report.rows.map((row, i) => (
                <tr key={`${i}-${String(row[0] ?? "")}`}>
                  {row.map((c, j) => (
                    <td key={`${report.headers[j]}-${i}`} className={isAmount(report.headers[j]) ? "num" : isQty(report.headers[j]) ? "qty" : ""}>
                      {String(c)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="footer">
          <div>
            <div>This is a computer-generated report. No signature required.</div>
            <div style={{ marginTop: 4 }}>Printed by MedOne MIS · {formatDate(new Date().toISOString())}</div>
          </div>
          <div className="signbox">Authorised Signatory</div>
        </div>
      </div>
    </div>
  );
}
